import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { IoFilter } from "react-icons/io5";
import { ResponsiveBar } from '@nivo/bar';


const ReportsSummary = ( { user, viewing, contacts, loans, opps } ) => {
    let params = useParams();
    const [ datePeriod, setDatePeriod ] = useState( 'This Year' );
    const [ startDate, setStartDate ] = useState( '' );
    const [ endDate, setEndDate ] = useState( '' );
    const [ segment, setSegment ] = useState( [] );
    const [ chartData, setChartData ] = useState( [] );
    
    const datePeriods = [ 'This Month', 'Last Month', 'This Quarter', 'Last Quarter', 'This Year', 'Last Year', 'Past Month', 'Past 3 Months', 'Past 6 Months', 'Past 9 Months', 'Past 12 Months', 'Custom' ];
    
    const isDateInRange = ( dateString, startDate, endDate ) => {
        if ( !dateString ) return false;
        const date = new Date( dateString.match( 'T' ) ? dateString : dateString + 'T12:00:00' ); // Parse the date string
        return date >= startDate && date <= endDate;
    };
    
    useEffect(() => {
        document.title = 'Third Floor - Summary Report';
    }, [] );

    useEffect( () => {
        let d = new Date();
        let currMonth = d.getMonth() + 1;

        switch ( datePeriod ) {
            case 'This Month':
                setStartDate( `${d.getFullYear()}-${currMonth.toString().padStart( 2, '0' )}-01` );
                setEndDate( d.toISOString().replace( /T.*/, '' ) );
                break;
                
            case 'Last Month':
                d.setDate( d.getDate() - d.getDate() );
                currMonth = d.getMonth() + 1;
                setStartDate( `${d.getFullYear()}-${currMonth.toString().padStart( 2, '0' )}-01` );
                setEndDate( d.toISOString().replace( /T.*/, '' ) );
                break;
                
            case 'This Quarter':
                break;
                
            case 'Last Quarter':
                break;
                
            case 'This Year':
                setStartDate( `${d.getFullYear()}-01-01` );
                setEndDate( d.toISOString().replace( /T.*/, '' ) );
                break;
                
            case 'Last Year':
                setStartDate( `${d.getFullYear()-1}-01-01` );
                setEndDate( `${d.getFullYear()-1}-12-31` );
                break;
                
            case 'Past Month':
                setEndDate( d.toISOString().replace( /T.*/, '' ) );
                d.setMonth( d.getMonth() - 1 );
                d.setDate( d.getDate() + 1 );
                setStartDate( d.toISOString().replace( /T.*/, '' ) );
                break;
                    
            case 'Past 3 Months':
                setEndDate( d.toISOString().replace( /T.*/, '' ) );
                d.setMonth( d.getMonth() - 3 );
                d.setDate( d.getDate() + 1 );
                setStartDate( d.toISOString().replace( /T.*/, '' ) );
                break;
                
            case 'Past 6 Months':
                setEndDate( d.toISOString().replace( /T.*/, '' ) );
                d.setMonth( d.getMonth() - 6 );
                d.setDate( d.getDate() + 1 );
                setStartDate( d.toISOString().replace( /T.*/, '' ) );
                break;
                
            case 'Past 9 Months':
                setEndDate( d.toISOString().replace( /T.*/, '' ) );
                d.setMonth( d.getMonth() - 9 );
                d.setDate( d.getDate() + 1 );
                setStartDate( d.toISOString().replace( /T.*/, '' ) );
                break;
                
            case 'Past 12 Months':
                setEndDate( d.toISOString().replace( /T.*/, '' ) );
                d.setMonth( d.getMonth() - 12 );
                d.setDate( d.getDate() + 1 );
                setStartDate( d.toISOString().replace( /T.*/, '' ) );
                break;
            
            case 'Custom':
                setStartDate( '' );
                setEndDate( '' );
                break;

            default:
        }
    }, [ datePeriod ] );

    useEffect( () => {
        const data = {
            leads: 0,
            fileStarts: 0,
            preQuals: 0,
            preApprovals: 0,
            creditPulls: 0,
            respa: 0,
            initDiscSent: 0,
            appraisalsOrdered: 0,
            locked: 0,
            processing: 0,
            uw: 0,
            initReview: 0,
            decisions: 0,
            resubmittals: 0,
            finalApprovals: 0,
            closingRequests: 0,
            funded: 0,
            volume: 0
        };

        if ( startDate && endDate ) {
            const sd = new Date( startDate );
            const ed = new Date( endDate );

            Object.keys( opps ).forEach( loid => {
                const o = opps[loid];

                if ( !o || !viewing.includes( o.aid ) ) {
                    return;
                }

                if ( isDateInRange( o.oppDate, sd, ed ) ) {
                    data.leads++;
                }
            } );

            Object.keys( loans ).forEach( lid => {
                const l = loans[lid];

                if ( !l || !viewing.includes( l.aid ) ) {
                    return;
                }

                let checked = false;
                const startFields = [ 'prospectDate', 'fileStartedDate', 'appStartedDate', 'appCompletedDate', 'appDocsUploadedDate' ];
                if ( startFields.some( f => {
                    if ( checked ) {
                        return false;
                    }
                    if ( l[f] && l[f].length ) {
                        checked = true;
                        return isDateInRange( l[f], sd, ed );
                    }
                    return false;
                } ) ) {
                    data.fileStarts++;
                }

                if ( isDateInRange( l.pqDate, sd, ed ) ) {
                    data.preQuals++;
                }

                if ( isDateInRange( l.paDate, sd, ed ) ) {
                    data.preApprovals++;
                }

                if ( isDateInRange( l.creditReportDate, sd, ed ) ) {
                    data.creditPulls++;
                }

                if ( isDateInRange( l.respaTriggeredDate, sd, ed ) ) {
                    data.respa++;
                }

                if ( isDateInRange( l.discSentDate, sd, ed ) ) {
                    data.initDiscSent++;
                }

                if ( isDateInRange( l.appraisalOrderedDate, sd, ed ) ) {
                    data.appraisalsOrdered++;
                }

                if ( isDateInRange( l.rateLockDate, sd, ed ) ) {
                    data.locked++;
                }

                if ( isDateInRange( l.inProcessingDate, sd, ed ) ) {
                    data.processing++;
                }

                if ( isDateInRange( l.inUWDate, sd, ed ) ) {
                    data.uw++;
                }

                if ( isDateInRange( l.conditionalApprovalDate, sd, ed ) ) {
                    data.initReview++;
                }

                // data.decisions++;

                if ( isDateInRange( l.conditionsSubmittedDate, sd,ed ) ) {
                    data.resubmittals++;
                }

                if ( isDateInRange( l.finalApprovalDate, sd,ed ) ) {
                    data.finalApprovals++;
                }

                if ( isDateInRange( l.inDocsDate, sd, ed ) ) {
                    data.closingRequests++;
                }

                if ( isDateInRange( l.fundedDate, sd, ed ) ) {
                    data.funded++;
                    data.volume += l.amount;
                }
            } );
        }

        setSegment( data );
    }, [ startDate, endDate, opps, loans, viewing ] );

    useEffect( () => {
        console.log( 'rid: ' + params.rid );
    }, [ params.rid ] );

    useEffect( () => {
        console.log( 'segment: ', segment );
        const chart = [
            { id: 'leads', label: 'Leads', value: segment.leads },
            { id: 'fileStarts', label: 'File Starts', value: segment.fileStarts },
            { id: 'preQuals', label: 'Pre-Quals', value: segment.preQuals },
            { id: 'preApprovals', label: 'Pre-Approvals', value: segment.preApprovals },
            { id: 'creditPulls', label: 'Credit Pulls', value: segment.creditPulls },
            { id: 'respa', label: 'RESPA', value: segment.respa },
            { id: 'initDiscSent', label: 'Initial Disclosures Sent', value: segment.initDiscSent },
            { id: 'appraisalsOrdered', label: 'Appraisals Ordered', value: segment.appraisalsOrdered },
            { id: 'locked', label: 'Locks', value: segment.locked },
            { id: 'processing', label: 'In Processing', value: segment.processing },
            { id: 'uw', label: 'In UW', value: segment.uw },
            { id: 'initReview', label: 'Initial Reviews', value: segment.initReview },
            // { id: 'decisions', label: 'decisions', value: segment.decisions },
            { id: 'resubmittals', label: 'Resubmittals', value: segment.resubmittals },
            { id: 'finalApprovals', label: 'Final Approvals', value: segment.finalApprovals },
            { id: 'closingRequests', label: 'Closing Requests', value: segment.closingRequests },
            { id: 'funded', label: 'Funded', value: segment.funded },
        ];
        setChartData( chart );
    }, [ segment ] );

    return (
        <Col className='ps-0' style={ { height: '100%' } }>
            <Row className='tf-section-container ms-2'>
                <Col>
                    <Row className='pb-2 border-bottom'>
                        <Col xs='auto' className='pe-2'>
                            <IoFilter size={22} />
                        </Col>
                        <Col className='fw-bold fs-6'>Raw Numbers</Col>
                    </Row>
                    <Row className='p-2 tf-report-filter-item position-relative align-items-center justify-content-start'>
                        <Col xs='auto' className='p-0 ps-4'>
                            <Form.Group as={Row} controlId='starterPeriod'>
                                <Col xs='auto'>
                                    <Form.Select size='sm' value={datePeriod} onChange={ ( e ) => setDatePeriod( e.target.value ) } aria-label=''>
                                        {
                                            datePeriods.map( ( obj, idx ) => <option key={`${obj}-${idx}`} value={obj}>{obj}</option> )
                                        }
                                    </Form.Select>
                                </Col>
                            </Form.Group>
                        </Col>
                        <Col xs='auto' className='p-0 ps-2'>
                            <Form.Group as={Row} controlId='startDate'>
                                <Col xs='auto'>
                                    <Form.Control type='date' value={startDate} onChange={ ( e ) => setStartDate( e.target.value ) } style={ { fontSize: '.875rem' } } />
                                </Col>
                            </Form.Group>
                        </Col>
                        <Col xs='auto' className='p-0 ps-2'>
                            -
                        </Col>
                        <Col xs='auto' className='p-0 ps-2'>
                            <Form.Group as={Row} controlId='endDate'>
                                <Col xs='auto'>
                                    <Form.Control type='date' value={endDate} onChange={ ( e ) => setEndDate( e.target.value ) } style={ { fontSize: '.875rem' } } />
                                </Col>
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className='tf-section-container ms-2'>
                <Col xs='auto' style={{
                    background: 'rgba(0, 115, 177, 0.1)',
                    borderRadius: '10px', // Rounds the corners
                    boxShadow: '0 4px 5px rgba(0, 0, 0, 0.4)', // Optional shadow for aesthetics
                    height: '350px',
                    width: '100%'
                }}>
                    <ResponsiveBar
                        data={chartData}
                        margin={{ top: 50, right: 10, bottom: 75, left: 10 }}
                        colors={{ scheme: 'oranges' }}
                        colorBy='indexValue'
                        indexBy='label'
                        borderColor={{
                            from: 'color',
                            modifiers: [
                                ['darker', 1],
                                ['opacity', .5]
                            ]
                        }}
                        labelColor={{ from: 'color', modifiers: [['darker', 2]] }}
                        labelPosition='end'
                        labelOffset={10}
                        axisBottom={{ enable: true, tickRotation: -30 }}
                        // motionConfig="wobbly"
                    />
                </Col>
            </Row>
        </Col>
    )
}

const mapProps = ( state ) =>( {
    user: state.user,
    viewing: state.user.viewing,
    contacts: state.contacts.byId,
    loans: state.loans.byId,
    opps: state.leadopps.byId
} );

const mapDispatch = ( dispatch ) => ( {
} );

export default connect( mapProps, mapDispatch )( ReportsSummary );